import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  MenuItem,
  Snackbar,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import './Login.css';

const ROLE_PATHS = {
  super_admin: '/Super-Dash',
  manager: '/Super-Dash',
  cb_executive: '/dashboard',
  Executive: '/Exec-Dash',
  default: '/dashboard',
};

const LoginPage = () => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
    role: '',
  });
  const [alert, setAlert] = useState({ open: false, type: 'success', message: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const value = e.target.value.replace(/[<>]/g, '');
    const field = e.target.name;

    console.log(`✏️ ${field} updated:`, value);
    setCredentials({ ...credentials, [field]: value });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleLogin = async () => {
    const { username, password, role } = credentials;

    if (!username || !password || !role) {
      console.warn('⚠️ Missing login fields:', { username, password, role });
      setAlert({ open: true, type: 'error', message: 'All fields are required' });
      return;
    }

    console.log('🔐 Attempting login with:', { username, role });
    setLoading(true);

    try {
      const response = await fetch('https://core-bankers-backend.vercel.app/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, role }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('❌ Login failed with status:', response.status);
        throw new Error(data.message || 'Login failed');
      }

      console.log('✅ Login successful:', {
        userId: data.id,
        username: data.username,
        role: data.role,
      });

      sessionStorage.setItem('token', data.token);
      sessionStorage.setItem('role', data.role);
      sessionStorage.setItem('userId', data.id);
      sessionStorage.setItem('username', data.username);

      setAlert({ open: true, type: 'success', message: `Welcome, ${data.role}` });

      const redirectPath = ROLE_PATHS[data.role] || ROLE_PATHS.default;
      console.log('➡️ Redirecting to:', redirectPath);
      navigate(redirectPath);
    } catch (err) {
      console.error('❌ Login error:', err.message);
      setAlert({ open: true, type: 'error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Paper className="login-card">
        <Typography variant="h5" className="login-title">
          Login
        </Typography>

        <TextField
          fullWidth
          name="username"
          label="Username"
          value={credentials.username}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          variant="outlined"
          className="login-field"
          disabled={loading}
        />

        <TextField
          fullWidth
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={credentials.password}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          variant="outlined"
          className="login-field"
          disabled={loading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={togglePasswordVisibility} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          fullWidth
          name="role"
          label="Select Role"
          value={credentials.role}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          className="login-field"
          helperText="Choose your role based on access level"
          disabled={loading}
        >
          <MenuItem value="executive">Financer</MenuItem>
          <MenuItem value="cb_executive">Executive</MenuItem>
          <MenuItem value="manager">Manager</MenuItem>
          <MenuItem value="super_admin">Super Admin</MenuItem>
        </TextField>

        <Button
          fullWidth
          variant="contained"
          color="primary"
          className="login-button"
          onClick={handleLogin}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Login'}
        </Button>
      </Paper>

      <Snackbar
        open={alert.open}
        autoHideDuration={3000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={alert.type} variant="filled">
          {alert.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default LoginPage;